import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, AlertTriangle, CheckCircle2, HelpCircle, Loader2 } from 'lucide-react';
import { creditNotesApi } from '../../services/creditNotesApi';
import { formatDate } from '../../utils/format';
import {
    formatAmount, formatCredited, formatCount, formatPercent,
    formatQuantity, formatSalesShare, isSalesShareCapped,
} from '../../utils/creditNoteFormat';
import { CreditNoteLinks } from './CreditNoteLinks';
import { ReasonPanel, ReviewPanel, RecoursePanel } from './CreditNoteStateForms';
import { CreditNoteTrail } from './CreditNoteTrail';
import {
    CREDIT_NOTE_RULES, RULE_EXPLANATIONS, RULE_LABELS,
    type CreditNoteDetail, type CreditNoteDocument, type CreditNoteLine, type CreditNoteRule,
} from '../../types/creditNotes';

/**
 * One credit note in full: what the rules found, what the ERP says, and why it was credited.
 *
 * THE UNJUDGEABLE DOCUMENTS ARE THE REASON THIS DRAWER HAS A "could not be judged" SECTION AT
 * ALL. 44 documents over 24 months can be judged by no rule, and that is a RESULT, not a gap:
 * a cancellation shell has no lines by construction, a zero-value document has nothing to
 * credit, a customer with no invoiced turnover offers no denominator. Without the named reason
 * on screen, a document with no verdict looks exactly like a document nobody checked — and the
 * whole point of a control page is that those two are never confused. The German text comes
 * down the wire from `CREDIT_NOTE_SKIP_LABELS`; this component never writes one itself.
 *
 * A DOCUMENT CAN FIRE ON ONE RULE AND BE UNJUDGEABLE ON ANOTHER, so the two sections are
 * independent and both can be non-empty at once. That is the common case for a credit note
 * without item lines.
 *
 * WHY THE SALES SHARE IS SHOWN AS TWO AMOUNTS. `salesSharePct` reaches 18'180'800 % — dit
 * `VNNC26-00049` and `VUNC26-00005`, each 1'818.08 against a customer whose 2026 turnover is a
 * single 0.01 EUR line. The list caps the ratio so it cannot break a column; here the two
 * amounts are printed instead, because "1'818.08 credited against 0.01 invoiced" is the only
 * form in which that finding reads as what it is.
 */

interface Props {
    doc: CreditNoteDocument;
    skipReasonLabels: Record<string, string>;
    /** Whether this user may write. The server refuses regardless; this only shapes the forms. */
    canEdit: boolean;
    onClose: () => void;
    onCopy: (value: string) => void;
    /**
     * A write happened. The list behind this drawer shows the reason, the booking status and the
     * recourse status in its own columns, and its KPI counts them — so it has to reload, or the
     * row the reviewer just changed keeps showing what the database no longer holds. A stale
     * panel beside a fresh one is the harder bug to notice: the screen is wrong while the
     * database is right.
     */
    onStateChanged: () => void;
}

const KIND_LABEL: Record<string, string> = {
    item: 'Item',
    charge: 'Item charge',
    gl: 'G/L account',
    comment: 'Comment',
    resource: 'Resource',
    fixedasset: 'Fixed asset',
    other: 'Other',
};

export function CreditNoteDrawer({ doc, skipReasonLabels, canEdit, onClose, onCopy, onStateChanged }: Props) {
    const [lines, setLines] = useState<CreditNoteLine[] | null>(null);
    const [comments, setComments] = useState<string[]>([]);
    const [linesLoading, setLinesLoading] = useState(true);
    const [linesError, setLinesError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLinesLoading(true);
        setLinesError(null);
        setLines(null);
        setComments([]);
        creditNotesApi.lines(doc.companyCode, doc.documentNo)
            .then(res => {
                if (cancelled) return;
                setLines(res.lines);
                setComments(res.commentLines);
            })
            .catch(err => {
                if (cancelled) return;
                setLinesError(err instanceof Error ? err.message : 'Failed to load the document lines');
            })
            .finally(() => { if (!cancelled) setLinesLoading(false); });
        return () => { cancelled = true; };
    }, [doc.companyCode, doc.documentNo]);

    /**
     * The app's own state: the reason, the two status tracks, the recourse cases and the trail.
     *
     * A SEPARATE READ FROM THE LINES, on purpose. The list row already carries a snapshot of this
     * state (`doc.app`) and the drawer could render from it — but the drawer is where it gets
     * EDITED, and the vocabularies, the catalogue and the full trail only come down here. Reading
     * it fresh on open also means a case a colleague created two minutes ago is on screen, rather
     * than whatever the list happened to load.
     */
    const [detail, setDetail] = useState<CreditNoteDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(true);
    const [detailError, setDetailError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setDetailLoading(true);
        setDetailError(null);
        setDetail(null);
        creditNotesApi.detail(doc.companyCode, doc.documentNo)
            .then(res => { if (!cancelled) setDetail(res); })
            .catch(err => {
                if (cancelled) return;
                setDetailError(err instanceof Error ? err.message : 'Failed to load the document state');
            })
            .finally(() => { if (!cancelled) setDetailLoading(false); });
        return () => { cancelled = true; };
    }, [doc.companyCode, doc.documentNo]);

    /**
     * Every write returns the WHOLE document state, so all three panels and the trail refresh
     * from one response — never just the field that was edited.
     */
    const applyWrite = useCallback((next: CreditNoteDetail) => {
        setDetail(next);
        onStateChanged();
    }, [onStateChanged]);

    const savers = useMemo(() => ({
        reason: (body: { reasonCode: string; comment: string | null; changeReason: string }) =>
            creditNotesApi.saveReason({ company: doc.companyCode, documentNo: doc.documentNo, ...body }),
        review: (body: { status: CreditNoteDetail['review']['status']; reviewDueAt: string | null; changeReason: string }) =>
            creditNotesApi.saveReview({ company: doc.companyCode, documentNo: doc.documentNo, ...body }),
        recourse: (body: Record<string, unknown>) =>
            creditNotesApi.saveRecourse({
                company: doc.companyCode,
                documentNo: doc.documentNo,
                ...body,
            } as Parameters<typeof creditNotesApi.saveRecourse>[0]),
    }), [doc.companyCode, doc.documentNo]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const { fired, wouldFire, notEvaluated } = doc.rules;
    const evaluatedClean = CREDIT_NOTE_RULES.filter(
        r => !fired.includes(r) && !wouldFire.includes(r) && !notEvaluated.some(n => n.rule === r)
    );

    const itemLines = (lines ?? []).filter(l => l.kind === 'item' || l.kind === 'charge');

    return (
        <div className="fixed inset-0 z-40 flex justify-end">
            <div className="flex-1 bg-black/20" onClick={onClose} />
            <aside className="flex h-full w-full max-w-3xl flex-col overflow-y-auto bg-gray-50 shadow-xl">

                {/* Header */}
                <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-baseline gap-2.5">
                                <span className="font-mono text-lg font-semibold text-gray-900">{doc.documentNo}</span>
                                <span className="text-[13px] text-gray-600">{doc.companyCode}</span>
                                {doc.origin === 'salestransactions' && (
                                    <span
                                        className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700"
                                        title="No ERP header knows this document — it exists only in etl.SalesTransactions. The company posts manual uploads."
                                    >
                                        Manual upload
                                    </span>
                                )}
                                {!doc.ledgerLinked && (
                                    <span
                                        className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600"
                                        title="No customer ledger entry — a cancellation shell (Source Code DELETE/ELIMINA)."
                                    >
                                        Cancellation shell
                                    </span>
                                )}
                            </div>
                            <div className="mt-1 text-sm text-gray-700">
                                {doc.customerName ?? <span className="text-gray-400">No customer on the header</span>}
                                {doc.customerNr && <span className="ml-1.5 text-xs text-gray-400">{doc.customerNr}</span>}
                            </div>
                            <div className="mt-0.5 text-xs text-gray-500">
                                Posted {formatDate(doc.postingDate)} · {doc.currencyCode}
                                {doc.salespersonCode && ` · ${doc.salespersonCode}`}
                                {doc.sourceType && ` · ${doc.sourceType}`}
                            </div>
                        </div>
                        <div className="flex shrink-0 items-start gap-3">
                            <div className="text-right">
                                <div className="text-xl font-semibold tabular-nums text-red-700">
                                    {formatCredited(doc.amountDocument)}
                                </div>
                                <div className="text-[11px] text-gray-400">
                                    {doc.amountEur === null
                                        ? 'no EUR rate'
                                        : `${formatCredited(doc.amountEur)} EUR${doc.amountEurIsExact ? '' : ' (approx.)'}`}
                                </div>
                            </div>
                            <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-3">
                        <CreditNoteLinks doc={doc} onCopy={onCopy} variant="inline" />
                        <span className="flex-1" />
                        <span className="font-mono text-[11px] text-gray-400">
                            {doc.companyCode}-{doc.documentNo}
                        </span>
                    </div>
                </div>

                <div className="flex flex-col gap-3.5 px-5 py-4">

                    {/* What the rules found */}
                    <section className="rounded-lg border border-gray-200 bg-white p-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            What the rules found
                        </p>

                        {fired.length === 0 && wouldFire.length === 0 && (
                            <div className="flex gap-2.5 pb-2">
                                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                                <p className="text-[13px] leading-5 text-gray-600">
                                    {evaluatedClean.length === 0
                                        ? 'No rule could be applied to this document — see below for why.'
                                        : `No rule objected. ${evaluatedClean.length} of ${CREDIT_NOTE_RULES.length} rules were applied and passed.`}
                                </p>
                            </div>
                        )}

                        {fired.map(rule => (
                            <RuleFinding
                                key={rule}
                                rule={rule}
                                doc={doc}
                                tone="fired"
                            />
                        ))}

                        {wouldFire.map(rule => (
                            <RuleFinding
                                key={rule}
                                rule={rule}
                                doc={doc}
                                tone="would"
                            />
                        ))}

                        {/* The named result, not a gap. */}
                        {notEvaluated.length > 0 && (
                            <div className="mt-3 border-t border-gray-100 pt-3">
                                <p className="mb-1.5 text-[11px] font-medium text-gray-500">
                                    Could not be judged — {notEvaluated.length} of {CREDIT_NOTE_RULES.length} rules
                                </p>
                                <ul className="space-y-1">
                                    {notEvaluated.map(n => (
                                        <li key={n.rule} className="flex gap-2.5">
                                            <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                                            <span className="text-[13px] leading-5 text-gray-600">
                                                <span className="font-medium text-gray-700">
                                                    {RULE_LABELS[n.rule] ?? n.rule}
                                                </span>
                                                {' — '}
                                                {/* German plain text, straight from the server. */}
                                                {skipReasonLabels[n.reason] ?? n.reason}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-2 text-[11px] italic leading-4 text-gray-500">
                                    This is a result, not a gap. A rule that cannot be applied has not cleared the
                                    document — and a document can fire on one rule while another cannot judge it.
                                </p>
                            </div>
                        )}
                    </section>

                    {/* What the ERP says */}
                    <section className="rounded-lg border border-gray-200 bg-white p-4">
                        <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            What the ERP says
                        </p>
                        <div className="grid grid-cols-3 gap-x-4 gap-y-2.5">
                            <Fact label="Credits invoice" value={doc.appliesToDocNo} mono />
                            <Fact label="External reference" value={doc.externalDocumentNo} />
                            <Fact label="Source code" value={doc.sourceCode} />
                            <Fact label="Document date" value={doc.documentDate ? formatDate(doc.documentDate) : null} />
                            <Fact label="Business unit" value={doc.businessUnitCode} />
                            <Fact label="ABC class" value={doc.abcClassification} />
                        </div>

                        {doc.subject && (
                            <div className="mt-3">
                                <p className="mb-1 text-[11px] font-medium text-gray-500">Subject on the header</p>
                                <p className="border-l-2 border-gray-200 pl-2.5 text-[13px] text-gray-700">{doc.subject}</p>
                            </div>
                        )}

                        {/* The free text is the real answer to "why", for most documents. */}
                        <div className="mt-3">
                            <p className="mb-1 text-[11px] font-medium text-gray-500">Comment lines on the document</p>
                            {linesLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                            ) : linesError ? (
                                <p className="text-[13px] text-red-700">{linesError}</p>
                            ) : comments.length === 0 ? (
                                <p className="text-[13px] text-gray-400">No comment lines on this document.</p>
                            ) : (
                                <div className="border-l-2 border-gray-200 pl-2.5">
                                    {comments.map((c, i) => (
                                        <div key={i} className="text-[13px] leading-5 text-gray-700">{c}</div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="mt-3">
                            <p className="mb-1 text-[11px] font-medium text-gray-500">
                                Item lines · {linesLoading ? '…' : formatCount(itemLines.length)}
                            </p>
                            {linesLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                            ) : itemLines.length === 0 ? (
                                <p className="text-[13px] text-gray-400">
                                    This credit note has no item line — nothing was credited against a delivered product.
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse text-xs">
                                        <thead>
                                            <tr className="border-b border-gray-200 text-left text-gray-500">
                                                <th className="py-1 pr-2 font-medium">Line</th>
                                                <th className="py-1 pr-2 font-medium">Type</th>
                                                <th className="py-1 pr-2 font-medium">Product</th>
                                                <th className="py-1 pr-2 text-right font-medium">Qty</th>
                                                <th className="py-1 pr-2 text-right font-medium">Price {doc.currencyCode}</th>
                                                <th className="py-1 text-right font-medium">Amount {doc.currencyCode}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itemLines.map(l => (
                                                <tr key={l.lineNo} className="border-b border-gray-50">
                                                    <td className="py-1.5 pr-2 font-mono text-gray-500">{l.lineNo}</td>
                                                    <td className="py-1.5 pr-2 text-gray-500">{KIND_LABEL[l.kind] ?? l.kind}</td>
                                                    <td className="py-1.5 pr-2 text-gray-700" title={l.itemNo ?? undefined}>
                                                        {l.description ?? l.itemNo ?? '–'}
                                                    </td>
                                                    <td className="py-1.5 pr-2 text-right tabular-nums text-gray-700">
                                                        {formatQuantity(l.quantity)}
                                                        {l.unitOfMeasure ? ` ${l.unitOfMeasure}` : ''}
                                                    </td>
                                                    <td className="py-1.5 pr-2 text-right tabular-nums text-gray-700">
                                                        {formatAmount(l.unitPrice)}
                                                    </td>
                                                    <td className="py-1.5 text-right tabular-nums text-gray-700">
                                                        {formatAmount(l.amount)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </section>

                    {/*
                        The write side. Read fresh from the server on open, and every panel
                        refreshed from every write's own response — the reason, both status
                        tracks and the trail come back together, so no panel can be showing what
                        the database no longer holds while its neighbour is right.
                    */}
                    {detailLoading ? (
                        <section className="rounded-lg border border-gray-200 bg-white p-4">
                            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                        </section>
                    ) : detailError ? (
                        <section className="rounded-lg border border-red-200 bg-red-50 p-4">
                            <p className="text-[13px] text-red-700">{detailError}</p>
                        </section>
                    ) : detail ? (
                        <>
                            <ReasonPanel
                                detail={detail}
                                canEdit={canEdit}
                                documentCurrency={doc.currencyCode}
                                onSaved={applyWrite}
                                save={savers}
                            />
                            {/* Two independent tracks (decision 5): a credit note can be booked
                                correctly and still be open against a supplier, so neither
                                verdict may imply the other. */}
                            <ReviewPanel
                                detail={detail}
                                canEdit={canEdit}
                                documentCurrency={doc.currencyCode}
                                onSaved={applyWrite}
                                save={savers}
                            />
                            <RecoursePanel
                                detail={detail}
                                canEdit={canEdit}
                                documentCurrency={doc.currencyCode}
                                onSaved={applyWrite}
                                save={savers}
                            />
                            <CreditNoteTrail history={detail.history} />
                        </>
                    ) : null}

                </div>
            </aside>
        </div>
    );
}

function Fact({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
    return (
        <div className="min-w-0">
            <div className="text-[11px] text-gray-500">{label}</div>
            <div className={`truncate text-[13px] text-gray-800 ${mono ? 'font-mono text-xs' : ''}`} title={value ?? undefined}>
                {value ?? <span className="text-gray-300">–</span>}
            </div>
        </div>
    );
}

/** One rule's verdict, with the numbers behind it rather than only the fact of it. */
function RuleFinding({ rule, doc, tone }: { rule: CreditNoteRule; doc: CreditNoteDocument; tone: 'fired' | 'would' }) {
    const s = doc.rules.signals;
    return (
        <div className="flex gap-2.5 border-b border-gray-100 py-2 last:border-b-0">
            <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${tone === 'fired' ? 'text-amber-600' : 'text-gray-400'}`} />
            <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-[13px] font-medium text-gray-900">{RULE_LABELS[rule]}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-gray-400">{rule}</span>
                    {tone === 'would' && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                            rule switched off for {doc.companyCode}
                        </span>
                    )}
                </div>
                <p className="mt-0.5 text-[13px] leading-5 text-gray-700">{RULE_EXPLANATIONS[rule]}</p>

                {rule === 'PRICE_DEVIATION' && s.priceDeviationPct !== null && (
                    <div className="mt-1 text-xs text-gray-600">
                        <span className="text-gray-400">Furthest line from the invoiced 24-month average: </span>
                        <span className="font-medium text-gray-800">{formatPercent(s.priceDeviationPct)}</span>
                    </div>
                )}

                {rule === 'LARGE_VS_SALES' && s.salesSharePct !== null && (
                    <div className="mt-1 grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
                        <div>
                            <span className="text-gray-400">Credited </span>
                            <span className="text-gray-800">{formatCredited(doc.amountEur)} EUR</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Invoiced that year </span>
                            <span className="text-gray-800">
                                {s.customerYearSalesEur === null ? '–' : `${formatAmount(s.customerYearSalesEur)} EUR`}
                            </span>
                        </div>
                        <div className="col-span-2">
                            <span className="text-gray-400">Share </span>
                            <span className="text-gray-800">{formatSalesShare(s.salesSharePct)}</span>
                            {isSalesShareCapped(s.salesSharePct) && (
                                <span className="ml-1.5 text-gray-500">
                                    — capped for display; the two amounts above are the finding.
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {rule === 'DUPLICATE' && s.duplicateDocumentNos.length > 0 && (
                    <div className="mt-1 text-xs text-gray-600">
                        <span className="text-gray-400">Same customer, amount and currency: </span>
                        <span className="font-mono text-gray-800">{s.duplicateDocumentNos.join(', ')}</span>
                    </div>
                )}

                {rule === 'QTY_NO_VALUE' && s.qtyNoValueLines > 0 && (
                    <div className="mt-1 text-xs text-gray-600">
                        <span className="text-gray-400">Lines with a quantity but no value: </span>
                        <span className="font-medium text-gray-800">{formatCount(s.qtyNoValueLines)}</span>
                    </div>
                )}
            </div>
        </div>
    );
}
